// A SMART ROOM's bar (door + motion sensor) — the room lock, the live sensors
// and the night settings, on the phone's room screen and the desktop room
// header. Renders NOTHING for any other room (owner, 2026-10-12: "all settings
// only when there is a door sensor and motion sensor in the same room"); the
// server refuses a lock there too.
//
//   🔒 Locked by Sanjay · since 10:42 PM      ← every user with access sees it
//   Door closed 12 min ago · motion 2 min ago
//   🚨 Alarm — Acknowledge                    ← while an intrusion is unanswered
//
// The watch itself is the server's (security.js): locked AND nobody inside
// (door closed, no motion after it) → a door opening or motion alerts the
// people the alert grid's 🔒 column names, until acknowledged or unlocked.

import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Tooltip, Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import TuneIcon from '@mui/icons-material/Tune';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import { useApp } from '../../core/state/AppContext';
import { ackIntrusion, setRoomLock } from '../../core/api/api';
import {
  lockCaption, sensorsCaption, roomVacancy, vacantFor,
} from '../../core/devices/roomPresence';
import { LOCKED_RED } from './RoomPresenceBadge';
import SmartRoomDialog from './SmartRoomDialog';
import { FIT_XS } from './fitDialog';

const errorText = (e, fallback) => e?.response?.data?.error || e?.message || fallback;

export default function SmartRoomBar({
  presence, roomId, roomName, locationId, onChanged, color, sx, compact = false,
}) {
  const { userId } = useApp();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(false);
  if (!presence) return null;

  const locked = presence.locked;
  const toggle = async () => {
    setBusy(true);
    setError('');
    try {
      await setRoomLock(roomId, !locked, userId);
      setConfirm(false);
      onChanged?.();
    } catch (e) {
      setError(errorText(e, 'Could not change the lock'));
    } finally {
      setBusy(false);
    }
  };
  const ack = async () => {
    try { await ackIntrusion(presence.locationId ?? locationId, userId); } catch (e) { /* shown again next poll */ }
    onChanged?.();
  };
  const textColor = color || 'text.primary';
  const live = sensorsCaption(presence);

  // COMPACT (the desktop room header, 2026-10-13): no caption line. The sensor
  // pills beside it already say "door closed 35 min / motion 33 min", so the
  // caption only repeated them; "Locked by … since …" moves to the tooltip.
  // Unlocked is the everyday state, so it is just the open-lock icon — the
  // word "Unlocked" would repeat the LOCATION's chip right above it.
  const tip = [locked ? lockCaption(presence) : 'Room unlocked — click to lock', live]
    .filter(Boolean).join(' · ');
  const chip = (
    <Chip
      size="small"
      icon={locked ? <LockIcon /> : <LockOpenIcon />}
      label={locked ? 'Locked' : (compact ? undefined : 'Unlocked')}
      onClick={() => setConfirm(true)}
      sx={{
        fontWeight: 700,
        bgcolor: locked ? LOCKED_RED : 'transparent',
        color: locked ? '#fff' : textColor,
        border: locked ? 'none' : '1px solid',
        borderColor: 'divider',
        '& .MuiChip-icon': { color: locked ? '#fff' : textColor },
        ...(compact && !locked ? { '& .MuiChip-label': { display: 'none' }, '& .MuiChip-icon': { mx: 0.5, color: textColor } } : {}),
      }}
    />
  );

  // ── VACANT · 1 h 40 min (owner, 2026-10-18) ───────────────────────────────
  // A smart room nobody is in says so, and since when (core/devices/roomPresence
  // roomVacancy: the later of the last motion and the door closing). Refreshed
  // with every presence poll (30 s).
  const vac = roomVacancy(presence);
  const vacantChip = vac.vacant && (
    <Tooltip
      title={vac.since
        ? `No one inside since ${new Date(vac.since).toLocaleString([], { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })} — the door closed after the last movement`
        : 'No one inside — the door closed after the last movement'}
    >
      <Chip
        size="small"
        icon={<PersonOffIcon />}
        label={vac.forMs != null ? `Vacant · ${vacantFor(vac.forMs)}` : 'Vacant'}
        sx={{
          fontWeight: 700, bgcolor: '#00897B', color: '#fff',
          '& .MuiChip-icon': { color: '#fff' },
        }}
      />
    </Tooltip>
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 0.25 : 1, flexWrap: 'wrap', minWidth: 0, ...sx }}>
      {compact ? <Tooltip title={tip}>{chip}</Tooltip> : chip}
      {vacantChip}
      {!compact && (
        <Box sx={{ minWidth: 0, flex: '1 1 160px' }}>
          {locked && (
            <Typography noWrap sx={{ fontSize: 12, fontWeight: 600, color: textColor }}>{lockCaption(presence)}</Typography>
          )}
          {live && <Typography noWrap sx={{ fontSize: 11, color: textColor, opacity: 0.75 }}>{live}</Typography>}
        </Box>
      )}
      {presence.openIncidents > 0 && (
        <Button size="small" variant="contained" color="error" onClick={ack} sx={{ fontWeight: 700 }}>
          🚨 Acknowledge
        </Button>
      )}
      <Tooltip title="Smart room settings">
        <IconButton size="small" onClick={() => setSettings(true)} sx={{ color: textColor }} aria-label="Smart room settings">
          <TuneIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog open={confirm} onClose={busy ? undefined : () => setConfirm(false)} {...FIT_XS}>
        <DialogTitle>{locked ? `Unlock ${roomName || 'this room'}?` : `Lock ${roomName || 'this room'}?`}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {locked
              ? 'The room stops being watched. Everyone with access sees it as unlocked.'
              : 'Everyone with access sees it as locked. While nobody is inside (door closed, no movement after it), a door opening or motion raises the alarm until someone acknowledges it or unlocks. Someone inside who moved after the door closed is never an alarm.'}
          </Typography>
          {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color={locked ? 'primary' : 'error'} onClick={toggle} disabled={busy}
            startIcon={busy ? <CircularProgress size={16} /> : (locked ? <LockOpenIcon /> : <LockIcon />)}
          >
            {locked ? 'Unlock' : 'Lock'}
          </Button>
        </DialogActions>
      </Dialog>

      {settings && (
        <SmartRoomDialog
          open
          onClose={() => setSettings(false)}
          presence={presence}
          roomId={roomId}
          roomName={roomName}
          locationId={presence.locationId ?? locationId}
          onSaved={onChanged}
        />
      )}
    </Box>
  );
}
